import React, { useState, useEffect } from 'react';
import { getOrders, updateOrder as localUpdateOrder, removeOrder, productById as localProductById, ORDER_STATUSES, statusById } from '../ordersStore';
import { fetchShop, updateOrder as shopUpdateOrder, removeOrder as shopRemoveOrder, getProductsCached } from '../shopStore';
import { getCustomer, getCustomers } from '../customerStore';
import { getDevices, updateDevice } from '../traccar';

const card = { background: 'white', border: '1px solid #e6eaf0', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' };
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isAssigned = (d) => { const a = d.attributes || {}; return !!a.customerId && a.account && a.account !== 'TA'; };

// Admin Orders queue. Every online purchase (and phone sale) lands here so the
// admin can provision devices onto the account and ship them, walking the order
// New → Provisioning → Shipped → Active.
export default function OrdersPage() {
  const [localOrders, setLocalOrders] = useState(getOrders());
  const [serverOrders, setServerOrders] = useState([]); // in-app Shop purchases (server-side)
  const [shopProducts, setShopProducts] = useState(getProductsCached());
  const [devices, setDevices] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all');

  // combined queue: server (in-app) orders + local (signup/phone) orders
  const orders = [...serverOrders, ...localOrders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  // product lookup + totals span BOTH catalogs (admin-defined shop + built-ins)
  const productById = (id) => shopProducts.find((p) => p.id === id) || localProductById(id);
  const orderTotal = (o) => (o.items || []).reduce((s, it) => { const p = productById(it.productId); return s + (p ? p.price * (it.qty || 1) : 0); }, 0);
  const orderDeviceCount = (o) => (o.items || []).reduce((n, it) => { const p = productById(it.productId); return n + (p ? (p.deviceCount || 0) * (it.qty || 1) : 0); }, 0);

  const refresh = () => setLocalOrders(getOrders());
  const loadServer = () => fetchShop().then((s) => { setServerOrders((s.orders || []).map((o) => ({ ...o, _server: true }))); setShopProducts(s.products); }).catch(() => {});
  useEffect(() => { getDevices().then(setDevices).catch(() => {}); loadServer(); }, []);
  useEffect(() => {
    const onFocus = () => { refresh(); loadServer(); getDevices().then(setDevices).catch(() => {}); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // route an update to the right store (server order vs local order)
  const updateOrder = (o, p) => {
    if (o._server) { setServerOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...p } : x))); shopUpdateOrder(o.id, p).then(loadServer).catch(() => {}); }
    else { localUpdateOrder(o.id, p); refresh(); }
  };

  const open = (o) => {
    setOpenId(openId === o.id ? null : o.id);
    if (!o.seen) updateOrder(o, { seen: true });
  };
  const setStatus = (o, status) => updateOrder(o, { status });
  const patch = (o, p) => updateOrder(o, p);
  const deleteOrder = (o) => {
    if (o._server) { setServerOrders((prev) => prev.filter((x) => x.id !== o.id)); shopRemoveOrder(o.id).then(loadServer).catch(() => {}); }
    else { removeOrder(o.id); refresh(); }
  };

  // provision: link an unassigned device to this order's account (same shape the
  // intake page uses), and record it on the order.
  const assignDevice = async (o, d) => {
    const cust = getCustomer(o.customerId) || getCustomers().find((c) => c.account === o.account);
    const acct = (cust && cust.account) || o.account;
    if (!acct) { alert('This order has no account number yet — open the customer in the Customers tab first.'); return; }
    const last4 = (d.uniqueId || '').slice(-4);
    const updated = { ...d, name: `${acct}-${last4}`, attributes: { ...(d.attributes || {}), customerId: (cust && cust.id) || '', account: acct, ownerUserId: (cust && cust.traccarUserId) || (d.attributes || {}).ownerUserId || '', claimed: true } };
    try {
      await updateDevice(updated);
      setDevices((ds) => ds.map((x) => (x.id === d.id ? updated : x)));
      const ids = Array.from(new Set([...(o.deviceIds || []), d.id]));
      updateOrder(o, { deviceIds: ids, status: o.status === 'new' ? 'provisioning' : o.status });
      refresh();
    } catch (e) { alert(`Could not assign the device: ${e.message}`); }
  };

  const filtered = filter === 'all' ? orders : orders.filter((o) => o.status === filter);
  const counts = ORDER_STATUSES.reduce((m, s) => { m[s.id] = orders.filter((o) => o.status === s.id).length; return m; }, {});
  const unassigned = devices.filter((d) => !isAssigned(d));

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0 }}>Orders</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>New purchases to provision & ship. {counts.new ? <b style={{ color: '#f97316' }}>{counts.new} new</b> : 'All caught up.'}</p>
        </div>
      </div>

      {/* status filter chips */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '4px 0 16px' }}>
        <Chip active={filter === 'all'} onClick={() => setFilter('all')} label={`All (${orders.length})`} color="#334155" />
        {ORDER_STATUSES.map((s) => <Chip key={s.id} active={filter === s.id} onClick={() => setFilter(s.id)} label={`${s.label} (${counts[s.id] || 0})`} color={s.color} />)}
      </div>

      {filtered.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: '#94a3b8', padding: '40px' }}>
          No orders here yet. Online purchases and phone sales will appear in this queue.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {filtered.map((o) => {
          const st = statusById(o.status);
          const devCount = orderDeviceCount(o);
          const provisioned = (o.deviceIds || []).length;
          const isOpen = openId === o.id;
          return (
            <div key={o.id} style={{ ...card, padding: 0, overflow: 'hidden', borderLeft: `4px solid ${st.color}` }}>
              {/* row */}
              <div onClick={() => open(o)} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', cursor: 'pointer', flexWrap: 'wrap' }}>
                {!o.seen && <span title="New" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f97316', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <div style={{ fontWeight: 800, color: '#0f172a' }}>{o.customerName || 'Unnamed customer'} {o.account && <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: '12px' }}>#{o.account}</span>}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{new Date(o.createdAt).toLocaleString()} · {o.source === 'phone' ? 'Phone order' : 'Online order'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, color: o.paid ? '#16a34a' : '#0f172a' }}>{money(o.amountPaid || orderTotal(o))}{!o.paid && <span style={{ fontSize: '11px', color: '#f97316', fontWeight: 700 }}> · unpaid</span>}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{devCount} device{devCount === 1 ? '' : 's'}{devCount ? ` · ${provisioned}/${devCount} provisioned` : ''}</div>
                </div>
                <span style={{ background: st.color, color: 'white', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '999px' }}>{st.label}</span>
                <span style={{ color: '#94a3b8' }}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {/* detail */}
              {isOpen && (
                <div style={{ borderTop: '1px solid #eef2f7', padding: '16px', background: '#f8fafc', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
                  {/* items + payment */}
                  <div style={card}>
                    <h4 style={{ margin: '0 0 8px' }}>Order</h4>
                    {(o.items || []).map((it, i) => { const p = productById(it.productId); if (!p) return null; return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <span>{p.name} × {it.qty || 1}</span><span style={{ fontWeight: 700 }}>{money(p.price * (it.qty || 1))}</span>
                      </div>
                    ); })}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, marginTop: '8px' }}>
                      <span>Total</span><span>{money(orderTotal(o))}</span>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                      <input type="checkbox" checked={!!o.paid} onChange={(e) => patch(o, { paid: e.target.checked, amountPaid: e.target.checked ? orderTotal(o) : o.amountPaid })} />
                      Payment received {o.paymentRef ? `(${o.paymentRef})` : ''}
                    </label>
                  </div>

                  {/* ship-to */}
                  <div style={card}>
                    <h4 style={{ margin: '0 0 8px' }}>Ship to</h4>
                    <div style={{ fontSize: '13px', color: '#334155', lineHeight: 1.6 }}>
                      {o.customerName}<br />
                      {o.address || <span style={{ color: '#94a3b8' }}>No address — add in Customers</span>}<br />
                      {[o.city, o.state, o.zip].filter(Boolean).join(', ')}<br />
                      {o.phone && <>📞 {o.phone}<br /></>}
                      {o.email && <>✉️ {o.email}</>}
                    </div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#64748b', marginTop: '10px' }}>Tracking #</label>
                    <input value={o.tracking || ''} onChange={(e) => patch(o, { tracking: e.target.value })} placeholder="Add when shipped" style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', marginTop: '4px' }} />
                  </div>

                  {/* provisioning */}
                  <div style={card}>
                    <h4 style={{ margin: '0 0 8px' }}>Provision devices <span style={{ color: '#94a3b8', fontWeight: 600 }}>({provisioned}/{devCount})</span></h4>
                    {(o.deviceIds || []).length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        {(o.deviceIds || []).map((id) => { const d = devices.find((x) => x.id === id); return (
                          <div key={id} style={{ fontSize: '13px', color: '#16a34a', fontWeight: 700 }}>✓ {d ? d.name : `Device ${id}`}</div>
                        ); })}
                      </div>
                    )}
                    {provisioned < devCount ? (
                      <>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Assign an unassigned device to account {o.account ? `#${o.account}` : '(set account first)'}:</div>
                        <div style={{ maxHeight: '140px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {unassigned.length === 0 && <span style={{ fontSize: '12px', color: '#94a3b8' }}>No unassigned devices in inventory. Intake one first.</span>}
                          {unassigned.map((d) => (
                            <button key={d.id} onClick={() => assignDevice(o, d)} style={{ textAlign: 'left', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', fontSize: '13px' }}>
                              ＋ {d.name} <span style={{ color: '#94a3b8' }}>· {(d.uniqueId || '').slice(-6)}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    ) : devCount > 0 ? (
                      <div style={{ fontSize: '13px', color: '#16a34a', fontWeight: 700 }}>All devices provisioned ✓</div>
                    ) : (
                      <div style={{ fontSize: '13px', color: '#64748b' }}>No hardware on this order (membership only).</div>
                    )}
                  </div>
                </div>
              )}

              {/* status pipeline actions */}
              {isOpen && (
                <div style={{ borderTop: '1px solid #eef2f7', padding: '12px 16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Move to:</span>
                  {ORDER_STATUSES.map((s) => (
                    <button key={s.id} onClick={() => setStatus(o, s.id)} disabled={o.status === s.id}
                      style={{ background: o.status === s.id ? s.color : 'white', color: o.status === s.id ? 'white' : s.color, border: `1px solid ${s.color}`, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 800, cursor: o.status === s.id ? 'default' : 'pointer' }}>{s.label}</button>
                  ))}
                  <button onClick={() => { if (window.confirm('Delete this order?')) deleteOrder(o); }} style={{ marginLeft: 'auto', background: 'white', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ active, onClick, label, color }) {
  return <button onClick={onClick} style={{ background: active ? color : 'white', color: active ? 'white' : color, border: `1px solid ${color}`, borderRadius: '999px', padding: '6px 14px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>{label}</button>;
}
